import React, { Fragment, Key, ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useQueryParams } from 'use-query-params'
import { ArrowLeftOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'

import { Show, getTagConfigMonitoringStatus } from '@app/components/util'
import { ContentLayout } from '@app/layouts'
import { LntButton, LntDropdown } from '@app/components/base'
import { RemoveListModal } from '@app/modals/RemoveListModal'
import {
  ExtendedTableWithSidebar,
  Header,
  PermissionWrapper,
  Subheader,
  SubheaderToolbarDivider,
  TableSettingsToolbar
} from '@app/components'

import {
  useToggle,
  useNotification,
  useErrorRedirect,
  useExtendedNavigate,
  usePaginationQueryParams,
  useTableSearch,
  useTableSetting,
  useMultiStatusMutation,
  useTableSize
} from '@app/hooks'

import { getExportFilesDropdownMenu, isNull } from '@app/utils'
import { ACCEPT_FILE_TYPES, FileUploadModal } from 'src/modals/FileUploadModal'
import { exportMonitoringListFile } from '@app/utils/export'
import { exportTemplateProductFile } from '@app/utils/export/exportFile'

import { PERMISSION } from '@app/constants/permission'
import { BUTTON_TYPE, SIZE } from '@app/constants'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { MONITORINGS_LISTS_KEY } from '@app/constants/queryKeys'
import { MONITORING_PRODUCT_STATUS, FILE_TYPES } from '@app/api/constants'

import { createInternalTasks } from '@app/api/routes/internalTasks'
import {
  deleteLists,
  getListsCurrentItem,
  getListsItemProducts,
  patchListsItemProduct,
  deleteListsItemsProducts,
  restoreListsItemsProducts,
  importListsItemsProductsFromFile,
  getMonitoringListsSummaryById,
  getQuotas,
  approveLists
} from '@app/api/routes/monitoringLists'
import {
  importMonitoringListsProducts,
  removeMonitoringListsProducts,
  updateMonitoringListsProducts
} from '@app/api/routes/monitoringListsProducts'

import { MonitoringListsProductDto } from '@app/api/routes/monitoringListsProducts/types'

import { RegistryTotalModal } from '@app/modals'

import { getProductHierarchy } from '@app/api/routes/productHierarchy'

import { getColumns, adaptRegistryCurrentListTableData } from './lib'
import { columnsSearchSchema, tableSearchQuerySchema, topFiltersQuerySchema } from './configs'
import {
  SendToApproveModal,
  PromoAlert,
  TopMenu,
  AddProductsDrawer,
  ComplexModeModal,
  ProductHierarchyTableSidebar,
  TopFilters,
  QuotaTitle
} from './ui'
import { useHowToMonitorColumn, useSubtypesColumn, useSwitchColumns, useToggleCurrentToolbarButtons } from './hooks'

import type { AxiosError } from 'axios'
import type { FiltersParams } from '@app/types/filter'
import type { ErrorResponseDto } from '@app/api/dtos'
import type { GetListsItemProducts } from '@app/api/routes/monitoringLists/types'
import type { ProductHierarchyTreeData } from '@app/pages/RegistryPageInner/ui/ProductHierarchyTableSidebar/types'
import type { MonitoringListDto, PromoDateInfoDto } from '@app/api/routes/monitoringLists/dtos'
import type { FileType } from '@app/types'
import type {
  SelectedHierarchy,
  PatchFnParams,
  SendToApproveParams,
  RegistryPageInnerProps,
  RegistryCurrentListDataSource
} from './types'

const columns = getColumns()

export const RegistryPageInner: React.FC<RegistryPageInnerProps> = ({ id }) => {
  const notification = useNotification()
  const navigate = useExtendedNavigate()
  const [title, setTitle] = useState('')

  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)
  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()

  const { size, onSizeToggle } = useTableSize(['middle', 'large'])

  const complexModeToggle = useToggle()
  const complexModeRemoveModalToggle = useToggle()
  const complexModeUpdateModalToggle = useToggle()
  const complexModeImportModalToggle = useToggle()

  const addProductDrawerToggle = useToggle()
  const importFromExcelToggle = useToggle()
  const removeListModalToggle = useToggle()
  const registryTotalModalToggle = useToggle()
  const sendToApproveModalToggle = useToggle()

  const buttonLoadingExportToggle = useToggle()

  const tableGroupToggle = useToggle()

  const { value: isShowColorsOnRows, toggle: toggleColorsOnRows } = useToggle(true)

  const [rowSelectionKeys, setRowSelectionKeys] = useState<Key[]>([])
  const [selectedDataRecords, setSelectedDataRecords] = useState<RegistryCurrentListDataSource[]>([])
  const [selectedHierarchy, setSelectedHierarchy] = useState<SelectedHierarchy | null>(null)

  const { deleteButtonToggle, restoreButtonToggle, handleUnselectToolbarSelectedButtons } =
    useToggleCurrentToolbarButtons(rowSelectionKeys, selectedDataRecords)

  const listItemProductsParams = useMemo<GetListsItemProducts>(
    () => ({ ...paginationParams, ...(queryFilters as GetListsItemProducts), ...selectedHierarchy, id }),
    [paginationParams, queryFilters, id, selectedHierarchy]
  )

  const queryClient = useQueryClient()

  const { data: dataListsItem, isError: isErrorDataListItem } = useQuery<
  MonitoringListDto,
  AxiosError<ErrorResponseDto>
  >({
    retry: false,
    queryKey: [MONITORINGS_LISTS_KEY.currentListItem, id],
    queryFn: async () => await getListsCurrentItem({ id })
  })

  const { data: quotasData } = useQuery({
    enabled: !isNull(selectedHierarchy),
    queryKey: [MONITORINGS_LISTS_KEY.quotas, selectedHierarchy, id],
    queryFn: async () => await getQuotas({ ...selectedHierarchy, id })
  })

  const { data: registryCurrentListTableData, isLoading: isDataListsItemProductsLoading } = useQuery({
    queryKey: [MONITORINGS_LISTS_KEY.listsItemProducts, listItemProductsParams],
    queryFn: async () => await getListsItemProducts(listItemProductsParams),
    select: registryCurrentListData => adaptRegistryCurrentListTableData(registryCurrentListData, isShowColorsOnRows)
  })

  const { data: productHierarchyData, isLoading: isProductHierarchyLoading } = useQuery({
    queryKey: [MONITORINGS_LISTS_KEY.productHierarchy, id],
    queryFn: async () => await getProductHierarchy(id)
  })

  const monitoringTypeId = useMemo(() => dataListsItem?.monitoringType.id, [dataListsItem])
  const promoDateInfo = useMemo<PromoDateInfoDto | null>(() => dataListsItem?.promo ?? null, [dataListsItem])

  const deleteListsMutation = useMutation({
    mutationFn: deleteLists,
    onSuccess: () => {
      notification.success({
        message: 'Успешно:',
        description: 'Список успешно удален!'
      })

      handleNavigateToRegistryPage()
    }
  })

  const {
    mutation: approveListsMutation,
    modalContextHolder,
    showErrorModal
  } = useMultiStatusMutation({
    mutationFn: approveLists,
    onSuccess: () => handleNavigateToRegistryPage()
  })

  const createInternalTasksMutation = useMutation(createInternalTasks, {
    onSuccess: () => {
      sendToApproveModalToggle.setUntoggled()

      notification.success({
        message: 'Успешно:',
        description: 'Рекомендации к изменению успешно отправлены на согласование!'
      })
    }
  })

  const patchListsItemProductMutation = useMutation({
    mutationFn: patchListsItemProduct,
    onSuccess: async () => {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.quotas] }),
        queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.currentListItem] }),
        queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.listsItemProducts] })
      ])
    }
  })

  const refetchAndResetTableState = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.listsItemProducts] })

    setRowSelectionKeys([])
    handleUnselectToolbarSelectedButtons()
  }

  const deleteListsItemsProductsMutation = useMutation({
    mutationFn: deleteListsItemsProducts,
    onSuccess: refetchAndResetTableState
  })

  const removeMonitoringListsProductsMutation = useMutation(removeMonitoringListsProducts)
  const updateMonitoringListsProductsMutation = useMutation(updateMonitoringListsProducts)
  const importMonitoringListsProductsMutation = useMutation(importMonitoringListsProducts)

  const restoreListsItemsProductsMutation = useMutation({
    mutationFn: restoreListsItemsProducts,
    onSuccess: refetchAndResetTableState
  })

  const monitoringListsProductDtoRef = useRef<MonitoringListsProductDto | null>(null)

  const { data: listSummaryData } = useQuery({
    queryKey: [MONITORINGS_LISTS_KEY.listSummary],
    queryFn: async () => await getMonitoringListsSummaryById({ id })
  })

  const handlePatchListsItemProduct = useCallback(
    ({ productId, ...body }: PatchFnParams): void => {
      if (complexModeToggle.value) {
        monitoringListsProductDtoRef.current = {
          id: productId,
          ...body
        }

        complexModeUpdateModalToggle.setToggled()

        return
      }

      patchListsItemProductMutation.mutate({
        id,
        productId,
        ...body
      })
    },
    [complexModeToggle.value]
  )

  const handleComplexModeUpdateProducts = (monitoringListIds: number[]): void => {
    if (!monitoringListsProductDtoRef.current) return

    updateMonitoringListsProductsMutation.mutate(
      { monitoringListIds: [...monitoringListIds, id], products: [monitoringListsProductDtoRef.current] },
      { onSettled: complexModeUpdateModalToggle.setUntoggled }
    )
  }

  const handleDeleteList = (): void => {
    deleteListsMutation.mutate({ ids: [id] })
  }

  const handleApprove = (): void => {
    approveListsMutation.mutate({ monitoringListIds: [id] })
  }

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handleNavigateToRegistryPage = (): void => navigate(-1)

  const handleProductAdded = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.listsItemProducts] })
    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.currentListItem] })
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleDeleteListsItemsProducts = async (): Promise<void> => {
    if (complexModeToggle.value) {
      complexModeRemoveModalToggle.setToggled()

      return
    }

    const productIds = selectedDataRecords
      .filter(record => record.status !== MONITORING_PRODUCT_STATUS.DELETED)
      .map(record => record.id)

    const data = {
      productIds
    }

    await deleteListsItemsProductsMutation.mutateAsync({
      id,
      data
    })

    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.currentListItem] })
  }

  const handleComplexModeRemoveProducts = (monitoringListIds: number[]): void => {
    const productIds = selectedDataRecords
      .filter(record => record.status !== MONITORING_PRODUCT_STATUS.DELETED)
      .map(record => record.id)

    removeMonitoringListsProductsMutation.mutate(
      { monitoringListIds: [...monitoringListIds, id], productIds },
      { onSuccess: refetchAndResetTableState, onSettled: complexModeRemoveModalToggle.setUntoggled }
    )
  }

  const handleRestoreListsItemsProducts = async (): Promise<void> => {
    const productIds = selectedDataRecords
      .filter(record => record.status === MONITORING_PRODUCT_STATUS.DELETED)
      .map(record => record.id)

    const data = {
      productIds
    }

    await restoreListsItemsProductsMutation.mutateAsync({
      id,
      data
    })
  }

  const handleSelectedRowChange = (selectedKeys: Key[], selectedRecords: RegistryCurrentListDataSource[]): void => {
    setRowSelectionKeys(selectedKeys)
    setSelectedDataRecords(selectedRecords)
  }

  const handleOnDownloadFromExcel = async (fileType: FileType): Promise<void> => {
    const fileName = `Шаблон_${title}`

    await exportTemplateProductFile(fileName, { id, file: fileType })
  }

  const monitoringListIdsRef = useRef<number[] | null>(null)

  const handleOpenImportFromExcelModal = (): void => {
    if (complexModeToggle.value) {
      complexModeImportModalToggle.setToggled()

      return
    }

    importFromExcelToggle.setToggled()
  }

  const handleComplexModeImportProducts = (monitoringListIds: number[]): void => {
    monitoringListIdsRef.current = monitoringListIds

    complexModeImportModalToggle.setUntoggled()

    importFromExcelToggle.setToggled()
  }

  const handleImportFromExcel = async (file: File | null): Promise<void> => {
    if (isNull(file)) return

    const pricesFormData = new FormData()

    pricesFormData.append('file', file, file.name)
    pricesFormData.append('fileFormat', FILE_TYPES.xlsx)

    if (complexModeToggle.value && monitoringListIdsRef.current) {
      pricesFormData.append('monitoringListIds', JSON.stringify([...monitoringListIdsRef.current, id]))
      await importMonitoringListsProductsMutation.mutateAsync(pricesFormData)
    } else {
      try {
        await importListsItemsProductsFromFile({ id }, pricesFormData)
      } catch (error: any) {
        showErrorModal(error)
      }
    }

    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.listsItemProducts] })

    importFromExcelToggle.setUntoggled()

    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.currentListItem] })
  }

  const handleExportListClick = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportToggle.setToggled()

    const fileName = 'Список_товаров'

    try {
      await exportMonitoringListFile(fileName, { id, file: fileType })
    } finally {
      buttonLoadingExportToggle.setUntoggled()
    }
  }

  const handleSendToApprove = async ({ executorUserId, comment }: SendToApproveParams = {}): Promise<void> => {
    await createInternalTasksMutation.mutateAsync({ monitoringListId: id, executorUserId, comment })
  }

  const handleProductHierarchySelect = (selectedNode: ProductHierarchyTreeData | null): void => {
    if (isNull(selectedNode)) return setSelectedHierarchy(selectedNode)

    const { queryParamKey, id } = selectedNode
    const newSelectedHierarchy = { [queryParamKey]: id }

    setSelectedHierarchy(newSelectedHierarchy as SelectedHierarchy)
  }

  const handleTableGroupToggle = (): void => {
    setSelectedHierarchy(null)
    tableGroupToggle.toggle()
  }

  useSwitchColumns({ columns, patchFn: handlePatchListsItemProduct })
  useHowToMonitorColumn({ columns, patchFn: handlePatchListsItemProduct })

  const columnsWithSubtypes = useSubtypesColumn({
    columns,
    monitoringList: dataListsItem,
    patchFn: handlePatchListsItemProduct
  })

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.registryPageInner, columnsWithSubtypes)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredListsItemProductsDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: registryCurrentListTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const exportDropdownItems = getExportFilesDropdownMenu(
    'Список товаров',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportListClick
  )

  const toolbar: ReactNode[] = [
    <Show
      key='delete-products-show'
      condition={ deleteButtonToggle.value }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.changeMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleDeleteListsItemsProducts }
        >
          Удалить
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Show>,

    <Show
      key='restore-products-show'
      condition={ restoreButtonToggle.value }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.changeMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleRestoreListsItemsProducts }
        >
          Восстановить
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Show>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSizeButtonProps={ { active: size === SIZE.large, onClick: onSizeToggle } }
      tableGroupButtonProps={ { active: tableGroupToggle.value, onClick: handleTableGroupToggle } }
      tableRowHighlightButtonProps={ { active: isShowColorsOnRows, onClick: toggleColorsOnRows } }
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  const subHeaderControl = (
    <div>
      <LntButton
        type={ BUTTON_TYPE.text }
        icon={ <ArrowLeftOutlined /> }
        size={ SIZE.small }
        onClick={ handleNavigateToRegistryPage }
      >
        К спискам
      </LntButton>
    </div>
  )

  const subheaderToolbar: ReactNode[] = [
    <PermissionWrapper
      key='complex-mode'
      requiredPermissions={ [PERMISSION.changeMonitoringList] }
    >
      <LntButton
        icon={ complexModeToggle.value ? <EyeOutlined /> : <EyeInvisibleOutlined /> }
        type={ complexModeToggle.value ? BUTTON_TYPE.secondary : BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ complexModeToggle.toggle }
      >
        Комплексный режим
      </LntButton>

      <SubheaderToolbarDivider />
    </PermissionWrapper>,

    <Fragment key='inner-registry-page-controls'>
      <PermissionWrapper requiredPermissions={ [PERMISSION.deleteMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.secondary }
          size={ SIZE.small }
          onClick={ removeListModalToggle.setToggled }
        >
          Удалить список
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.exportMonitoringList] }>
        <LntDropdown
          trigger={ ['click'] }
          items={ exportDropdownItems }
        >
          <LntButton
            key='export-dropdown'
            type={ BUTTON_TYPE.secondary }
            size={ SIZE.small }
            loading={ buttonLoadingExportToggle.value }
          >
            Экспорт списка
          </LntButton>
        </LntDropdown>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.approveMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.primary }
          size={ SIZE.small }
          onClick={ handleApprove }
        >
          Согласовать
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.recommendationToChangeMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.primary }
          size={ SIZE.small }
          onClick={ async () => await handleSendToApprove() }
        >
          Отправить на согласование
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.recommendationToChangeMonitoringListWithUserChoice] }>
        <LntButton
          type={ BUTTON_TYPE.primary }
          size={ SIZE.small }
          onClick={ sendToApproveModalToggle.setToggled }
        >
          Отправить на согласование
        </LntButton>
      </PermissionWrapper>
    </Fragment>
  ]

  useErrorRedirect(isErrorDataListItem)

  const header = useMemo(() => {
    const monitoringTypeName: string = dataListsItem?.monitoringType?.name ?? ''
    const storeName: string | undefined = dataListsItem?.store?.name
    const clusterName: string | undefined = dataListsItem?.cluster?.name
    const clustersGroupName: string | undefined = dataListsItem?.clustersGroup?.name
    const statuses = [getTagConfigMonitoringStatus(dataListsItem?.status ?? 'NEW')]

    const title = `${monitoringTypeName}, ${storeName ?? clusterName ?? clustersGroupName ?? ''}`
    setTitle(title)

    return <Header controlProps={ { title, statuses } } />
  }, [dataListsItem])

  return (
    <>
      { header }

      <Subheader
        control={ subHeaderControl }
        toolbar={ subheaderToolbar }
      />

      <ContentLayout>
        <Show condition={ !!promoDateInfo }>
          <PromoAlert { ...promoDateInfo } />
        </Show>

        <TopMenu
          onDownloadFromExcel={ handleOnDownloadFromExcel }
          openImportFromExcelModal={ handleOpenImportFromExcelModal }
          openProductsFilter={ addProductDrawerToggle.setToggled }
          openRegistryTotalModal={ registryTotalModalToggle.setToggled }
        />

        <TopFilters
          onSearch={ handleFiltersChange }
          initialFilters={ queryFilters as FiltersParams }
          monitoringTypeId={ monitoringTypeId }
        />

        <ExtendedTableWithSidebar
          size={ size }
          isSidebarOpen={ tableGroupToggle.value }
          loading={ isDataListsItemProductsLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredListsItemProductsDataSource }
          control={ <QuotaTitle quotas={ quotasData } /> }
          toolbar={ toolbar }
          rowSelection={ {
            selectedRowKeys: rowSelectionKeys,
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: registryCurrentListTableData?.count,
            onChange: handlePaginationChange
          } }
          sidebar={
            <ProductHierarchyTableSidebar
              isLoading={ isProductHierarchyLoading }
              productHierarchyData={ productHierarchyData }
              onSelect={ handleProductHierarchySelect }
            />
          }
        />
      </ContentLayout>

      <AddProductsDrawer
        id={ id }
        isOpen={ addProductDrawerToggle.value }
        isComplexMode={ complexModeToggle.value }
        onClose={ addProductDrawerToggle.setUntoggled }
        onProductAdded={ handleProductAdded }
      />

      <FileUploadModal
        title='Импорт из excel'
        accept={ ACCEPT_FILE_TYPES.xlsx }
        open={ importFromExcelToggle.value }
        onCancel={ importFromExcelToggle.setUntoggled }
        onFileUpload={ handleImportFromExcel }
      />

      <RemoveListModal
        open={ removeListModalToggle.value }
        onCancel={ removeListModalToggle.setUntoggled }
        onRemoveList={ handleDeleteList }
        title='Вы действительно хотите удалить список?'
      />

      <ComplexModeModal
        title='Удалить товары из'
        submitText='Удалить'
        open={ complexModeRemoveModalToggle.value }
        onSubmit={ handleComplexModeRemoveProducts }
        onCancel={ complexModeRemoveModalToggle.setUntoggled }
      />

      <ComplexModeModal
        title='Изменить товары в'
        submitText='Изменить'
        open={ complexModeUpdateModalToggle.value }
        onSubmit={ handleComplexModeUpdateProducts }
        onCancel={ complexModeUpdateModalToggle.setUntoggled }
      />

      <ComplexModeModal
        title='Импорт товаров в'
        submitText='Далее'
        initialMonitoringTypeIds={ monitoringTypeId ? [monitoringTypeId] : undefined }
        open={ complexModeImportModalToggle.value }
        onSubmit={ handleComplexModeImportProducts }
        onCancel={ complexModeImportModalToggle.setUntoggled }
      />

      <RegistryTotalModal
        title='Итого по списку'
        summaryEntity={ listSummaryData }
        open={ registryTotalModalToggle.value }
        onCancel={ registryTotalModalToggle.setUntoggled }
      />

      <SendToApproveModal
        open={ sendToApproveModalToggle.value }
        onCancel={ sendToApproveModalToggle.setUntoggled }
        onSendToApprove={ handleSendToApprove }
      />

      { modalContextHolder }
    </>
  )
}
